import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwj508Api, ApprovalCandidate } from '../../services/pwj508Api';
import './PWJ508Form.css';

interface ItemState {
  item: ApprovalCandidate;
  selected: boolean;
}

const PWJ508Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);

  const selectedCount = items.filter(s => s.selected).length;
  const allSelected = items.length > 0 && items.every(s => s.selected);
  const someSelected = items.some(s => s.selected) && !allSelected;

  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwj508Api.queryCandidates(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map(item => ({ item, selected: false }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的繳庫單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => { if (e.key === 'Enter') onQuery(); },
    [onQuery]
  );

  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems(prev => prev.map(s => s.item.fj190_01 === no ? { ...s, selected: checked } : s));
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems(prev => prev.map(s => ({ ...s, selected: checked })));
  }, []);

  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要核准的繳庫單');
      return;
    }
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆繳庫單？此操作不可復原。`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items.filter(s => s.selected).map(s => s.item.fj190_01);
        setExecuting(true);
        try {
          const res = await pwj508Api.approve(selectedNos);
          if (res.failed === 0) {
            message.success(res.message || `核准完成，成功 ${res.success} 筆`);
          } else {
            message.warning(res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`);
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach(err => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '核准執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={e => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={e => handleToggle(record.item.fj190_01, e.target.checked)}
        />
      ),
    },
    { title: '繳庫單號', dataIndex: ['item', 'fj190_01'], key: 'fj190_01', width: 120 },
    {
      title: '繳庫日期',
      dataIndex: ['item', 'fj190_11'],
      key: 'fj190_11',
      width: 110,
      render: (v: string) => v?.substring(0, 10) || '',
    },
    { title: '庫別名稱', dataIndex: ['item', 'fj190_12'], key: 'fj190_12', width: 120 },
    { title: '入單人員', dataIndex: ['item', 'fj190_23'], key: 'fj190_23', width: 90 },
    { title: '核准狀態', dataIndex: ['item', 'fj190_80'], key: 'fj190_80', width: 80 },
  ];

  return (
    <div className="pwj508-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '核准執行中...' : '載入中...'}
        </div>
      )}
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>研發管理</a>
              <span>/</span>
              <span className="current">研發繳庫單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>研發繳庫單核准</h1>
            <span className="badge">PWJ508</span>
          </div>
        </div>

        <div className="query-bar">
          <label>繳庫單號 .從:</label>
          <Input
            value={startNo}
            onChange={e => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={e => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>，已勾選 <strong>{selectedCount}</strong> 筆</span>
                )}
              </div>
            </div>
          )}
          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={record => record.item.fj190_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入繳庫單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWJ508Form;
